import ConsoleTransport from './transports/ConsoleTransport.mjs';
import {
  hasProp,
  isEvent,
  isLogLevelString,
  isObject,
  isObjectWithEntry,
  isString
} from './validation.mjs';

const logLevels = {
  error: 0,
  warn: 1,
  info: 2,
  debug: 3
};

const logLevelToEventType = {
  error: 'logerror',
  warn: 'logwarn',
  info: 'loginfo',
  debug: 'logdebug'
};

const loggableError = (err) => {
  const keepFields = [
    'message',
    'name',
    'stack',
    'code',
    'status',
    'details',
    'retryable'
  ];

  const loggableErr = {};

  keepFields.forEach( (key) => {
    /* eslint-disable-next-line security/detect-object-injection */
    if (typeof err[key] !== 'undefined') {
      /* eslint-disable-next-line security/detect-object-injection */
      loggableErr[key] = err[key];
    }
  });

  return loggableErr;
};

const formatArg = (arg) => {
  if (arg instanceof Error) {
    return loggableError(arg);
  }
  return arg;
};

const formatDataFromArgs = (args) => {
  const formatted = args.map(formatArg);
  return (formatted.length === 1) ? formatted[0] : formatted;
};

const getNodes = (nodes) => {
  /* eslint-disable-next-line no-undef */
  if (nodes instanceof NodeList) {
    return Array.from(nodes);
  }
  /* eslint-disable-next-line no-undef */
  if (nodes instanceof Node) {
    return [ nodes ];
  }
  /* eslint-disable-next-line no-undef */
  if (Array.isArray(nodes) && nodes.every( (node) => node instanceof Node) ) {
    return nodes;
  }

  throw new TypeError(
    'nodeOrNodeList parameter of Logger.registerNodes must be an instance of Node or NodeList type'
  );
};

const validateEventTypeMap = (eventTypeMap) => {
  if (!isObjectWithEntry(eventTypeMap)) {
    throw new TypeError('eventTypeMap parameter of Logger.registerNodes must be a non-empty object');
  }

  Object.values(eventTypeMap).forEach( (level) => {
    if (!hasProp(logLevels, level)) {
      throw new TypeError('eventTypeMap parameter of must have valid log level strings for all map values');
    }
  });
};

/**
 * Standard Logger class.  This logger exposes
 *
 * @class
 * @method child
 * @method debug
 * @method deregisterNodes
 * @method error
 * @method info
 * @method log
 * @method registerNodes
 * @method warn
 * @static defaultOpts
 * @static initialize
 */
/* eslint-disable-next-line no-undef */
class Logger extends EventTarget {
  /** @constructor */
  constructor(opts) {
    super();

    // set logger opts
    const _opts = Object.freeze(Object.assign({}, this.constructor.defaultOpts, opts));
    this.constructor.validateOpts(_opts);

    Object.defineProperty(this, '_opts', {
      value: _opts
    });

    // set active log levels
    /* eslint-disable-next-line security/detect-object-injection */
    const maxLogLevel = logLevels[this._opts.level];
    /* eslint-disable-next-line security/detect-object-injection */
    const activeLevels = Object.keys(logLevels).filter( (level) => logLevels[level] <= maxLogLevel );
    Object.defineProperty(this, '_activeLevels', {
      value: activeLevels
    });

    // build functions for use as DOM Node listeners
    const domListenerFns = {};
    activeLevels.forEach( (level) => {
      /* eslint-disable-next-line security/detect-object-injection */
      domListenerFns[level] = (event) => {
        // validation to prevent potential object injection from unknown events
        if (!isEvent(event)) {
          throw new TypeError('invalid event received');
        }
        const data = {};
        for (const key in event) {
          /* eslint-disable-next-line security/detect-object-injection */
          data[key] = formatArg(event[key]);
        }
        /* eslint-disable-next-line security/detect-object-injection */
        this[level](`${event.type} Event`, data);
      };
    });
    Object.freeze(domListenerFns);
    Object.defineProperty(this, '_domListenerFns', {
      value: domListenerFns
    });

    // _buildEvent function
    Object.defineProperty(this, '_buildEvent', {
      value: (level, args) => {
        const eventData = { level };
        // if first item in args is string, we treat it as the message for the data

        if (isString(args[0]) && args[0].length > 0) {
          eventData.message = args.shift();
        }

        if (args.length > 0) {
          eventData.data = formatDataFromArgs(args);
        }

        if (Object.keys(_opts.context).length > 0)  {
          eventData.context = _opts.context;
        }

        /* eslint-disable-next-line no-undef, security/detect-object-injection */
        return new CustomEvent(logLevelToEventType[level], { detail: eventData });
      }
    });

    // for supported log level and transport combination, we deploy event listeners
    _opts.transports.forEach( (transport) => {
      // for each event type that the transport supports, we attach a listener to this logger
      activeLevels.forEach( (level) => {
        /* eslint-disable-next-line security/detect-object-injection */
        this.addEventListener(logLevelToEventType[level], transport, { passive: true });
      });
    });
  }

  /**
   * Method to create a child logger with additional context added.  The method operates by performing Object.assign()
   * of passed context object onto the existing context option value for the instance on which this method is being called.
   * A new logger instance is then created based on the updated set of options. All other options (including transports)
   * are applied to the child instance.
   *
   * @param context
   * @returns {Logger}
   */
  child(context = {}) {
    context = Object.assign({}, this._opts.context, context);
    const opts = Object.assign({}, this._opts, { context });
    /* c8 ignore next */
    return new this.constructor(opts);
  }

  /**
   * Debug-level logging method
   *
   * @param {string|Object|Array} args
   */
  debug(...args) {
    this.log('debug', ...args);
  }

  /**
   * Method to deregister event listeners node and event combination previously registered with the logger.
   * The same node(s) and event type map passed in initial register request should be re-used if you need to guarantee
   * removal of all previously installed listeners.
   *
   * @param {Node|NodeList|Array<Node>} nodeLike
   * @param {Object} eventTypeMap
   * @throws TypeError
   */
  deregisterNodes(nodeLike, eventTypeMap) {
    const nodes = getNodes(nodeLike);
    validateEventTypeMap(eventTypeMap);

    const eventTypes = Object.keys(eventTypeMap);

    nodes.forEach( (node) => {
      eventTypes.forEach( (type) => {
        /* eslint-disable-next-line security/detect-object-injection */
        const level = eventTypeMap[type];

        // attach only is DOM listener function is active for given log level
        /* eslint-disable-next-line security/detect-object-injection */
        const listener = this._domListenerFns[level];
        if (listener) {
          node.removeEventListener(type, listener);
        }
      });
    });
  }

  /**
   * Error-level logging method
   *
   * @param {string|Object|Array} args
   */
  error(...args) {
    this.log('error', ...args);
  }

  /**
   * Info-level logging method
   *
   * @param {string|Object|Array} args
   */
  info(...args) {
    this.log('info', ...args);
  }

  /**
   * Method which performs actual log event dispatch and is called by level-specific methods with first parameter
   * set to the level string. If first argument is not a string matching one of the log levels, the defaultLogLevel
   * configuration will be applied as level of the message.
   *
   * Generally, use of the level-specific methods is recommended.
   *
   * @param {string|Object|Array} maybeLevel
   * @param {string|Object|Array} [args]
   */
  log(maybeLevel, ...args) {
    // do nothing if no args passed
    /* c8 ignore next */
    if (maybeLevel === undefined) return;

    let level = this._opts.defaultLogLevel;

    // if first argument is level, set level for message, otherwise put first argument in array with rest of args
    if (isLogLevelString(maybeLevel)) {
      level = maybeLevel;
    } else {
      args.unshift(maybeLevel);
    }

    // do nothing if logger is not configured to log at the given level or there are no args (i.e. nothing to log)
    /* c8 ignore next 2 */
    /* eslint-disable-next-line security/detect-object-injection */
    if (logLevels[level] > logLevels[this._opts.level] || args.length === 0) return;

    const event = this._buildEvent(level, args);

    this.dispatchEvent(event);
  }

  registerGlobalHandlers(windowObj = null) {
    /* c8 ignore next 2 */
    /* eslint-disable-next-line no-undef */
    let target = (typeof window !== 'undefined') ? window : windowObj;
    const listener = this._domListenerFns.error;
    if (listener && target) {
      target.addEventListener('error', listener, { passive: true });
      target.addEventListener('unhandledrejection', listener, { passive: true });
    }
  }

  /**
   * Method to register DOM nodes to enable event logging based on a configurable map of event types to log levels.
   * A listener is added to the passed node(s) for each event type.
   *
   * @param {Node|NodeList|Array<Node>} nodeLike
   * @param {Object} eventTypeMap
   * @throws TypeError
   */
  registerNodes(nodeLike, eventTypeMap) {
    const nodes = getNodes(nodeLike);
    validateEventTypeMap(eventTypeMap);

    const eventTypes = Object.keys(eventTypeMap);

    nodes.forEach( (node) => {
      eventTypes.forEach( (type) => {
        /* eslint-disable-next-line security/detect-object-injection */
        const level = eventTypeMap[type];
        /* eslint-disable-next-line security/detect-object-injection */
        const listener = this._domListenerFns[level];
        if (listener) {
          node.addEventListener(type, listener, { passive: true });
        }
      });
    });
  }

  /**
   * Warn-level logging method
   *
   * @param {string|Object|Array} args
   */
  warn(...args) {
    this.log('warn', ...args);
  }

  /**
   * Static factory method for class.
   * Doesn't really do anything for this base Logger.
   *
   * @param {LoggerOptions} opts
   * @returns {Logger}
   */
  static initialize(opts = {}) {
    return new this(opts);
  }

  /**
   * Method to statically determine if a set of options meets validation criteria for the class.
   *
   * @param {LoggerOptions} opts
   */
  static validateOpts(opts) {
    if (!isObject(opts.context)) {
      throw new TypeError('Invalid or missing context option');
    }
    if (!isLogLevelString(opts.defaultLogLevel)) {
      throw new TypeError('Invalid or missing defaultLogLevel option');
    }
    if (!isLogLevelString(opts.level)) {
      throw new TypeError('Invalid or missing defaultLogLevel option');
    }
    if (!Array.isArray(opts.transports)) {
      throw new TypeError('Invalid or missing defaultLogLevel option');
    }
    if (opts.transports.length === 0) {
      throw new TypeError('No transports have been specified');
    }
  }
}

/**
 * @typedef LoggerOptions
 * @prop {Object} [context] key-value map of stored context information
 * @prop {string} [defaultLogLevel] setting for log level to be applied to events that do not have specified level
 * (for example, if calling .log() method directly)
 * @prop {string} [level] Threshold log level for the logger. For example, 'warn' would log for 'error' and 'warn',
 * while 'debug' would log for all levels
 * @prop {Transport[]} transports Array of Transport objects to be used as listeners to log events
 *
 * @type LoggerOptions
 */
Logger.defaultOpts = Object.freeze({
  context: {},
  defaultLogLevel: 'debug',
  level: 'warn',
  transports: [
    ConsoleTransport.initialize()
  ]
});

export {
  Logger as default,
  Logger
};
